<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          General Properties
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button" tabindex="0" @keyup.enter="editPanel()"
                  @click="editPanel()"
                  :class="{ disabled: editOtherPanels || loading || loadingError }"
                  v-if="!editCurrentPanel"
                  title="Edit"
                >
                  <font-awesome-icon :icon="['far', 'edit']" size="lg"/>
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-loader v-if="loading"/>
      <es-exception
        v-else-if="loadingError"
        style="margin: 0"
        exceptionMessage="There was an issue.
        Please try again or contact your administrator."
      />
      <es-form v-else style="padding: 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="header-elements">
              <font-awesome-icon :icon="['fas', 'cog']" size="lg"/>
            </div>
            <strong class="title is-6">
              Default Properties
            </strong>
          </div>
        </template>
        <template #es-form>
          <div class="general-properties-form-container">
            <div class="general-properties-row" :class="{ 'no-border': editCurrentPanel }">
              <div class="property has-text-grey">Assignment / Indexing Mode</div>
              <div class="editable">
                <div class="control">
                  <es-loader v-if="!identityControl || identityControl.LIFETIME_IDS == null" size="2x"/>
                  <input v-else type="text"
                    class="input is-static has-text-right  is-pulled-right"
                    :class="{'is-readonly' : editCurrentPanel}"
                    readonly
                    :value="assignmentMode"
                    :aria-label="assignmentMode"
                  />
                </div>
              </div>
            </div>
            <div class="general-properties-row" :class="{ 'no-border': editCurrentPanel }">
              <div class="property has-text-grey">
                Extract: Downloadable Types Allowed
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
              </div>
              <div  class="editable">
                <div class="control">
                    <es-select
                      v-if="editCurrentPanel"
                      class="is-pulled-right"
                      v-on:update:values="extractDownloadAllowed = $event"
                      valueTpl="${description}"
                      :setValues="submissionTypes"
                      keyValue="code"
                      :multi="true"
                      :preSelected="extractDownloadAllowed"
                    />
                    <div
                      v-else-if="extractDownloadAllowed.length"
                      class="tags is-pulled-right"
                      v-for="(item, index) in sortedExtractDownloadAllowed"
                      :key="index"
                    >
                      <strong class="tag source-tag">
                        {{ item.description }}
                      </strong>&nbsp;
                    </div>
                    <div v-else class="tags is-pulled-right">
                      <strong class="tag source-tag">
                        N/A
                      </strong>&nbsp;
                    </div>
                  </div>
              </div>
            </div>
            <div class="general-properties-row" :class="{ 'no-border': editCurrentPanel }">
              <div class="property has-text-grey">Maximum Duplicate Records</div>
              <div class="editable">
                <div class="control">
                    <input
                      class="input has-text-right  is-pulled-right"
                      :class="{
                        'is-static': !editCurrentPanel,
                        'is-danger' : hasErrorMaxDupRecords,
                      }"
                      @keypress="checkInput($event)"
                      @keyup.prevent="validateField('maxDupRecords')"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.MAX_DUP_QUEUE_RECORDS"
                      :aria-label="form.MAX_DUP_QUEUE_RECORDS || 'Maximum Duplicate Queue Records'"
                    >
                  </div>
                  <span v-if="hasErrorMaxDupRecords" class="help is-danger">
                    {{ msgMaxDupRecords }}
                  </span>
              </div>
            </div>
            <div class="general-properties-row" :class="{ 'no-border': editCurrentPanel }">
              <div class="property has-text-grey">Download Record Type</div>
              <div class="editable">
                <div class="control">
                    <div
                      class="buttons has-addons is-pulled-right"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in downloadRecordTypeOptions"
                        :key="index"
                        @click="form.DOWNLOAD_RECORD_TYPE = option.description"
                        :class="{ 'is-active': form.DOWNLOAD_RECORD_TYPE === option.description }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-right  is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :value="form.DOWNLOAD_RECORD_TYPE"
                      :readonly="!editCurrentPanel"
                      :aria-label="form.DOWNLOAD_RECORD_TYPE || 'Download Record Type'"
                    >
                  </div>
              </div>
            </div>
          </div>
        </template>
      </es-form>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCog,
} from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faCog,
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsSelect,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.downloadRecordTypeOptions = [
      {
        description: 'Master',
      },
      {
        description: 'Submission',
      },
    ];
    this.enabledDisabled = [
      {
        code: true,
        description: 'Enabled',
      },
      {
        code: false,
        description: 'Disabled',
      },
    ];
  },
  mounted() {
    this.onLoad();
    this.updateCache();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    assignmentMode() {
      return JSON.parse(this.identityControl.LIFETIME_IDS.toString().toLowerCase()) === true
        ? 'Lifetime ID' : 'Context ID';
    },
    sortedExtractDownloadAllowed() {
      function compare(a, b) {
        if (a.code < b.code) {
          return -1;
        }
        if (a.code > b.code) {
          return 1;
        }
        return 0;
      }
      return this.extractDownloadAllowed.slice().sort(compare).reverse();
    },
  },
  methods: {
    ...mapActions({
      initProperties: 'propertiesModule/init',
    }),
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;
        const res = await axios.get('submission-types');
        this.submissionTypes = res.data.filter(a => a.active === true);
      } catch (err) {
        this.loadingError = true;
        this.messages.push('There were some errors. Please try again. If errors persist please contact support.');
        this.showNotification('danger', this.messages);
      } finally {
        this.loading = false;
      }
      this.initialize(this.properties);
    },
    initialize(data) {
      this.form = this.prepForDisplay('form', this.groupBySection(data, 'PERSON-'));
      this.form.ENABLE_SPECIAL_EDU_AGENCY = JSON.parse(this.form.ENABLE_SPECIAL_EDU_AGENCY);
      this.identityControl = this.prepForDisplay('identityControl', this.groupBySection(data, 'IDENTITY_CONTROL-'));
      this.parseExtractDownloadTypesAllowed();
    },
    parseExtractDownloadTypesAllowed() {
      if (this.form.EXTRACT_DOWNLOAD_TYPES_ALLOWED != null) {
        const parsedSubmissionTypes = JSON.parse(this.form.EXTRACT_DOWNLOAD_TYPES_ALLOWED);
        this.extractDownloadAllowed = this.submissionTypes
          .filter(i => parsedSubmissionTypes.includes(i.code));
      } else {
        this.extractDownloadAllowed = [];
      }
      this.updateCache();
    },
    cachedDownloadTypes() {
      this.extractDownloadAllowed = this.cachedExtractDownloadAllowed;
    },
    updateCache() {
      this.cache = JSON.parse(JSON.stringify(this.form));
      this.cachedExtractDownloadAllowed = this.extractDownloadAllowed;
    },
    prepForDisplay(type, arrFromDb) {
      const o = {};
      arrFromDb.map((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        o[key] = value;
        return 0;
      });
      return o;
    },
    groupBySection(data, type) {
      return Object.entries(data)
        .filter(([key]) => key.includes(type))
        .map(([key, val]) => ({ [key.replace(type, '')]: val }));
    },
    cancelPanel() {
      this.undoForm();
      this.cachedDownloadTypes();
      this.hideNotification();
      this.editCurrentPanel = false;
      this.$emit('allowEditOtherPanels');
    },
    checkInput(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      }
    },
    undoForm() {
      this.form = JSON.parse(JSON.stringify(this.cache));
    },
    editPanel() {
      this.editCurrentPanel = true;
      this.$emit('allowEditOtherPanels');
    },
    initialData() {
      return {
        cache: {},
        form: {},
        identityControl: {},
        editCurrentPanel: false,
        loading: false,
        loadingError: false,
        cachedExtractDownloadAllowed: [],
        extractDownloadAllowed: [],
        submissionTypes: [],
        hasErrorMaxDupRecords: false,
        msgMaxDupRecords: null,
      };
    },
    checkForFormChanges() {
      return this.compareObjects(this.cache, this.form);
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    prepForRequest() {
      const payload = [];
      let cached = null;
      let obj = null;
      obj = this.form;
      cached = this.cache;
      this.pushToPayLoad('PERSON', payload, obj, cached);
      return payload;
    },
    pushToPayLoad(category, payload, obj, cached) {
      Object.entries(obj).forEach(([property, value]) => {
        if (value !== cached[property]) {
          payload.push({ category, property, value });
        }
      });
    },
    async updatePanel() {
      this.$emit('allowEditOtherPanels');
      if (!this.extractDownloadAllowed.length) {
        this.form.EXTRACT_DOWNLOAD_TYPES_ALLOWED = null;
      } else {
        this.form.EXTRACT_DOWNLOAD_TYPES_ALLOWED = JSON.stringify(
          this.extractDownloadAllowed.map(type => type.code),
        );
      }
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      if (this.hasErrorMaxDupRecords) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        return;
      }
      try {
        await axios({
          method: 'put',
          url: '/properties',
          data: this.prepForRequest(),
        });
        this.updateCache();
        await this.initProperties();
        this.showNotification('success', ['The General Properties have been updated successfully.']);
        this.editCurrentPanel = false;
      } catch (error) {
        if (error.response.status === 400) {
          this.apiMsg = 'There was an issue. Please try again or contact your administrator.';
        } else {
          this.apiMsg = 'There were some errors. Please fix the errors highlighted in the fields below.';
        }
        this.showNotification('danger', [this.apiMsg]);
      }
    },
    validateField(field) {
      if (field === 'maxDupRecords') {
        if (!this.form.MAX_DUP_QUEUE_RECORDS) {
          this.hasErrorMaxDupRecords = true;
          this.msgMaxDupRecords = 'Maximum Duplicate Records is required.';
        } else if (this.form.MAX_DUP_QUEUE_RECORDS
            && (this.form.MAX_DUP_QUEUE_RECORDS < 1
            || this.form.MAX_DUP_QUEUE_RECORDS > 100)) {
          this.hasErrorMaxDupRecords = true;
          this.msgMaxDupRecords = 'Maximum Duplicate Records must be between 1 and 100.';
        } else {
          this.hasErrorMaxDupRecords = false;
          this.msgMaxDupRecords = null;
        }
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    vertical-align: middle
    &:first-child
      border: 0
      width: 30px
  table th
    vertical-align: middle
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .property
    padding-left: 0
    font-size: 12px
    width: 75%
  .top-row
    padding-top: 1rem
  .no-border
    border: 0
  .header-elements
    margin: 0px 7px !important
  .general-properties-form-container
    overflow-x: auto
    overflow-y: hidden
    .general-properties-row
      margin-left: 30px
      display: flex
      flex-direction: row
      flex-wrap: nowrap
      align-items: center
      min-width: max-content
      padding: 6px 9px 6px 0
      &:not(:last-child)
        border: 1px solid #dbdbdb;
        border-width: 0 0 1px;
      .editable
        width: 50%
        flex: 0 0 auto
      .property
        text-align: left
</style>
